import { zodResolver } from '@hookform/resolvers/zod';
import { staleTimes, useQuery } from '@platform/api-client';
import { Input, Select } from '@platform/design-system';
import type { TypedClient } from '@platform/openapi';
import { useEffect, useState } from 'react';
import { useForm } from 'react-hook-form';
import { z } from 'zod';
import { nameIn } from '../catalog/categories.ts';
import {
  MAX_MOVEMENT_NOTE_LENGTH,
  MOVEMENT_KINDS,
  type Location,
  type RecordMovementBody,
} from './inventory.ts';
import { FormSheet } from '../FormSheet.tsx';
import { refineSearchLabel } from '../RefineHint.tsx';
import { SearchField } from '../SearchField.tsx';
import { queryKeys, usePageContext } from '../usePageContext.ts';

const SEARCH_RESULTS = 20;

const formSchema = z.object({
  variantId: z.uuid(),
  locationId: z.uuid(),
  kind: z.enum(MOVEMENT_KINDS),
  quantity: z.number().int().positive(),
  note: z.string().max(MAX_MOVEMENT_NOTE_LENGTH),
});

export type MovementFormValues = z.infer<typeof formSchema>;

export type MovementSheetProps = {
  open: boolean;
  api: TypedClient;
  locations: readonly Location[];
  submitting: boolean;
  errorMessage: string | null;
  onClose: () => void;
  onSubmit: (body: RecordMovementBody) => void;
};

const useVariantSearch = (api: TypedClient, search: string | null) => {
  const { tenantId, locale } = usePageContext();
  const found = useQuery({
    queryKey: queryKeys.products(tenantId, { view: 'movement-picker', search }),
    queryFn: () =>
      api.get('/admin/products', {
        query: { search: search ?? '', status: 'all', page_size: SEARCH_RESULTS },
      }),
    enabled: search !== null,
    staleTime: staleTimes.catalog,
  });
  const products = found.data?.items ?? [];
  return {
    shown: products.length,
    total: found.data?.total ?? 0,
    options: products.flatMap((product) =>
      product.variants.map((variant) => ({
        value: variant.id,
        label: `${nameIn(product, locale, product.id)} · ${variant.sku}`,
      })),
    ),
  };
};

export const MovementSheet = ({
  open,
  api,
  locations,
  submitting,
  errorMessage,
  onClose,
  onSubmit,
}: MovementSheetProps) => {
  const { t, locale } = usePageContext();
  const [search, setSearch] = useState<string | null>(null);
  const variants = useVariantSearch(api, search);
  const variantOptions = variants.options;
  const locationOptions = locations.map((location) => ({ value: location.id, label: location.name }));
  const defaults: MovementFormValues = {
    variantId: '',
    locationId: locationOptions[0]?.value ?? '',
    kind: 'restock',
    quantity: 1,
    note: '',
  };
  const form = useForm<MovementFormValues>({ resolver: zodResolver(formSchema), defaultValues: defaults });
  const firstVariant = variantOptions[0]?.value ?? '';
  useEffect(() => {
    form.setValue('variantId', firstVariant);
  }, [form, firstVariant]);

  return (
    <FormSheet
      open={open}
      formId="movement-form"
      title={t('inventory.newMovement')}
      submitting={submitting}
      errorMessage={errorMessage}
      onClose={onClose}
      onSubmit={(event) => {
        void form.handleSubmit((values) => {
          onSubmit({ ...values, note: values.note.trim() === '' ? null : values.note.trim() });
        })(event);
      }}
    >
      <SearchField
        label={t('inventory.searchVariant')}
        buttonLabel={t('inventory.search')}
        onSearch={setSearch}
      />
      <Select
        label={t('inventory.variant')}
        options={variantOptions}
        placeholder={
          variantOptions.length === 0
            ? t(search === null ? 'inventory.searchFirst' : 'inventory.noVariants')
            : undefined
        }
        helperText={refineSearchLabel(t, locale, variants.shown, variants.total)}
        errorText={form.formState.errors.variantId ? t('inventory.variantRequired') : undefined}
        {...form.register('variantId')}
      />
      <Select
        label={t('inventory.depot')}
        options={locationOptions}
        placeholder={locationOptions.length === 0 ? t('inventory.noDepots') : undefined}
        errorText={form.formState.errors.locationId ? t('inventory.depotRequired') : undefined}
        {...form.register('locationId')}
      />
      <Select
        label={t('inventory.kind')}
        options={MOVEMENT_KINDS.map((kind) => ({ value: kind, label: t(`inventory.kind_${kind}`) }))}
        {...form.register('kind')}
      />
      <Input
        label={t('inventory.quantity')}
        type="number"
        min={1}
        required
        helperText={t('inventory.quantityHelp')}
        errorText={form.formState.errors.quantity ? t('inventory.quantityRequired') : undefined}
        {...form.register('quantity', { valueAsNumber: true })}
      />
      <Input label={t('inventory.note')} maxLength={MAX_MOVEMENT_NOTE_LENGTH} {...form.register('note')} />
    </FormSheet>
  );
};
